/**
 * Cancellable backoff for the app's retry loops.
 *
 * A retry loop that plainly `await delay(...)`s between attempts has no way to
 * learn that nobody wants the result any more: it wakes on a timer and fires the
 * next request into whatever world exists by then. Two things go wrong with that:
 *
 * - **The caller gave up.** An aborted request (unmount, latest-wins guard) still
 *   sleeps out the full backoff before the abort surfaces, so the loader holds a
 *   skeleton for a result that was already discarded.
 * - **The world went away.** Under Vitest, jsdom is torn down per test *file*
 *   while node's timers keep running. A retry scheduled by a finished test wakes
 *   into a dead environment, `axios`'s XHR adapter finds no `XMLHttpRequest`, and
 *   the rejection lands with nothing left to catch it — Vitest reports an
 *   unhandled rejection and exits 1 with every assertion green (#4200).
 *
 * `waitBeforeRetry` fixes both: it reports whether the retry should still run,
 * and {@link abortPendingRetries} cancels every pending backoff outright.
 */

/**
 * The slice of `AbortSignal` a backoff needs. Structural (rather than
 * `AbortSignal`) so axios' `GenericAbortSignal` — the type of
 * `AxiosRequestConfig["signal"]` — passes straight through.
 */
export type RetryCancelSignal = {
  readonly aborted?: boolean;
  addEventListener?(type: "abort", listener: () => void): void;
  removeEventListener?(type: "abort", listener: () => void): void;
};

/** Cancel functions for the backoffs currently sleeping. */
const pendingBackoffs = new Set<() => void>();

/**
 * Sleep `ms`, then report whether the retry that scheduled it should still run.
 *
 * Resolves `true` on a normal wake-up and `false` when the caller aborted during
 * the sleep — a caller that aborted wants its error now, not one pointless retry
 * later. A backoff cancelled by {@link abortPendingRetries} **never settles**:
 * see that function for why silence is the right answer there.
 */
export function waitBeforeRetry(ms: number, signal?: RetryCancelSignal): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    let onCallerAbort: (() => void) | undefined;

    // Clears the timer and listeners without settling — settling is each
    // call site's decision below.
    const dispose = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      if (onCallerAbort) signal?.removeEventListener?.("abort", onCallerAbort);
      pendingBackoffs.delete(dispose);
    };

    onCallerAbort = () => {
      dispose();
      resolve(false);
    };
    timer = setTimeout(() => {
      dispose();
      resolve(true);
    }, ms);

    signal?.addEventListener?.("abort", onCallerAbort);
    pendingBackoffs.add(dispose);
  });
}

/**
 * Cancel every sleeping backoff, for a host environment that is going away.
 *
 * Today's only caller is the Vitest teardown (`vitest.setup.ts`), which runs it
 * after each test so no retry can wake into a jsdom that no longer exists
 * (#4200). The browser has no equivalent moment — a real page teardown takes the
 * whole JS context with it.
 *
 * A cancelled backoff leaves its promise **permanently unsettled**, so the retry
 * loop above it simply stops. That is deliberate: rejecting would surface as an
 * unhandled rejection in the very environment we are dismantling, because the
 * caller that would have caught it is already gone — which is the failure this
 * exists to prevent, not a fix for it. Nothing can be stranded by the silence:
 * the only thing still holding the promise is code from a world that has ended.
 */
export function abortPendingRetries(): void {
  // Each `dispose` removes only itself, and removing the element being visited
  // is safe mid-iteration; `clear()` is belt-and-braces.
  for (const dispose of pendingBackoffs) dispose();
  pendingBackoffs.clear();
}
